/**
 * Per-person memory of the inbox tab (kanban 778fe5cf, usage-graph change 3).
 *
 * The inbox opened on My Important every time, and in 90 days of production
 * use Roblyn switched to Unmapped on 79 of her 115 tab picks, Emily on 23 of
 * her 55. The unmapped count badge was already on the tab, so the badge was
 * not what stopped the switch. The last tab each person chose is kept per
 * user and reapplied when the inbox opens without a deep link.
 *
 * Precedence when the page mounts (highest first):
 *   1. `?tab=<value>` in the URL: a shared deep link means exactly that.
 *   2. `?emailId=<uuid>` in the URL: an email deep link opens that email on
 *      My Important, the view the cross-view fetch lands it in.
 *   3. The remembered tab for the signed-in user.
 *   4. `important` (My Important).
 *
 * Storage is the shared per-user localStorage memory (perUserMemory.ts).
 */

import type { TabFilter } from '../../components/portal/FilterTabs';
import { createPerUserMemory } from './perUserMemory';

/** The tabs the inbox actually builds (inboxTabUtils.buildInboxTabs). */
export type InboxTab = Extract<TabFilter, 'important' | 'all-important' | 'unmapped'>;

export const INBOX_TAB_VALUES: InboxTab[] = ['important', 'all-important', 'unmapped'];

export function isInboxTab(v: string | null | undefined): v is InboxTab {
  return typeof v === 'string' && (INBOX_TAB_VALUES as string[]).includes(v);
}

/**
 * Why the page opened on the tab it did. Carried on the `inbox_tab` portal
 * event as `source` so the usage graph can tell a restored tab from one the
 * person clicked (`chosen`).
 */
export type InboxTabSource = 'url' | 'email_link' | 'restored' | 'default';

export interface InitialInboxTab {
  tab: InboxTab;
  source: InboxTabSource;
}

const memory = createPerUserMemory<InboxTab>({
  storagePrefix: 'cm_portal_inbox_tab:',
  isValid: isInboxTab,
});

export function inboxTabStorageKey(userId: string): string {
  return memory.storageKey(userId);
}

/** The tab this user last chose on this browser, or null if none / invalid. */
export function readRememberedInboxTab(userId: string | null | undefined): InboxTab | null {
  return memory.read(userId);
}

/** Remember the tab this user just chose. `important` is remembered too: it is a choice. */
export function rememberInboxTab(userId: string | null | undefined, tab: InboxTab): void {
  memory.remember(userId, tab);
}

/**
 * Pure precedence rule for the tab the inbox opens on. See the module
 * comment for the order. `remembered` is whatever `readRememberedInboxTab`
 * returned so the rule can be tested without a DOM.
 */
export function resolveInitialInboxTab(input: {
  urlTab: string | null;
  urlEmailId: string | null;
  remembered: InboxTab | null;
}): InitialInboxTab {
  if (isInboxTab(input.urlTab)) {
    return { tab: input.urlTab, source: 'url' };
  }
  if (input.urlEmailId) {
    return { tab: 'important', source: 'email_link' };
  }
  if (input.remembered && input.remembered !== 'important') {
    return { tab: input.remembered, source: 'restored' };
  }
  return { tab: 'important', source: 'default' };
}
